import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Save, RefreshCw, Bell, BellOff } from 'lucide-react';

interface NotificationSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'candidate' | 'job' | 'company' | 'match';
  entityId: string;
  entityName: string;
}

// Define notification types for each entity type
const getNotificationTypes = (entityType: string) => {
  switch (entityType) {
    case 'candidate':
      return {
        contact_info: 'Contact Information Updates',
        availability: 'Availability Changes',
        new_document: 'New Documents Added',
        new_process: 'New Recruitment Process',
        new_comment: 'New Comments',
        status_change: 'Status Changes',
        skill_updates: 'Skill Updates',
        ai_analysis: 'AI Analysis Complete'
      };
    case 'job':
      return {
        status_change: 'Status Changes',
        new_application: 'New Applications',
        application_update: 'Application Updates',
        new_comment: 'New Comments',
        deadline_approaching: 'Deadline Approaching',
        ai_analysis: 'AI Analysis Complete',
        info_changes: 'Job Information Updates',
        published_status: 'Published/Unpublished Status'
      };
    case 'company':
      return {
        info_updates: 'Company Information Updates',
        contact_update: 'Contact Updates',
        new_job: 'New Jobs Created',
        new_comment: 'New Comments'
      };
    case 'match':
      return {
        status_change: 'Status Changes',
        new_comment: 'New Comments',
        ai_analysis: 'AI Analysis Complete',
        interview_scheduled: 'Interview Scheduled'
      };
    default:
      return {};
  }
};

export const NotificationSubscriptionModal: React.FC<NotificationSubscriptionModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityName
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [subscriptions, setSubscriptions] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);

  const notificationTypes = getNotificationTypes(entityType);

  // Load current subscription
  useEffect(() => {
    if (isOpen && user) {
      loadSubscription();
    }
  }, [isOpen, user, entityType, entityId]);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('notification_subscriptions')
        .select('subscription_types')
        .eq('user_id', user?.id)
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .maybeSingle();

      if (error) {
        console.error('Error loading subscription:', error);
        return;
      }

      if (data) {
        setSubscriptions(data.subscription_types || {});
        setHasSubscription(true);
      } else {
        // Initialize with all types enabled by default
        const defaultSubscriptions: Record<string, boolean> = {};
        Object.keys(notificationTypes).forEach(key => {
          defaultSubscriptions[key] = true;
        });
        setSubscriptions(defaultSubscriptions);
        setHasSubscription(false);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSubscription = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .rpc('upsert_user_entity_subscription', {
          p_user_id: user.id,
          p_entity_type: entityType,
          p_entity_id: entityId,
          p_subscription_types: subscriptions
        });

      if (error) throw error;

      setHasSubscription(true);
      toast({
        title: t('notifications.subscription.saved', 'Subscription saved'),
        description: t('notifications.subscription.savedDescription', 'Your notification preferences have been updated.'),
      });
      
      onClose();
    } catch (error) {
      console.error('Error saving subscription:', error);
      toast({
        title: t('notifications.subscription.error', 'Error'),
        description: t('notifications.subscription.saveError', 'Failed to save subscription. Please try again.'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key: string, value: boolean) => {
    setSubscriptions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const toggleAll = (enabled: boolean) => {
    const newSubscriptions: Record<string, boolean> = {};
    Object.keys(notificationTypes).forEach(key => {
      newSubscriptions[key] = enabled;
    });
    setSubscriptions(newSubscriptions);
  };

  const resetToDefaults = () => {
    const defaultSubscriptions: Record<string, boolean> = {};
    Object.keys(notificationTypes).forEach(key => {
      defaultSubscriptions[key] = true;
    });
    setSubscriptions(defaultSubscriptions);
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('notifications.subscription.loading', 'Loading...')}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin" />
            <span className="ml-2">{t('notifications.subscription.loading', 'Loading preferences...')}</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            {t('notifications.subscription.title', 'Notification Settings')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            {t('notifications.subscription.description', 'Choose which notifications you want to receive for')} <strong>{entityName}</strong>
            <br />
            <span className="text-xs text-blue-500">
              {t('notifications.subscription.overrideNote', 'Note: These settings override your global notification preferences for this specific entity.')}
            </span>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleAll(true)}
              className="flex items-center gap-1"
            >
              <Bell className="h-3 w-3" />
              {t('notifications.subscription.enableAll', 'Enable All')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleAll(false)}
              className="flex items-center gap-1"
            >
              <BellOff className="h-3 w-3" />
              {t('notifications.subscription.disableAll', 'Disable All')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={resetToDefaults}
            >
              {t('notifications.subscription.reset', 'Reset')}
            </Button>
          </div>

          <Separator />

          {/* Notification Types */}
          <div className="space-y-3">
            {Object.entries(notificationTypes).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor={key} className="text-sm font-medium">
                    {label}
                  </Label>
                </div>
                <Switch
                  id={key}
                  checked={subscriptions[key] || false}
                  onCheckedChange={(checked) => handleToggle(key, checked)}
                />
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={saveSubscription}
              disabled={saving}
            >
              {saving ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving', 'Saving...')}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {t('common.save', 'Save')}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 